import styled from "styled-components";
import { useState, useRef, useEffect, CSSProperties } from "react";
import { AiOutlineArrowRight, AiOutlineArrowLeft } from 'react-icons/ai'

import {
    add,
    eachDayOfInterval,
    endOfMonth,
    format,
    getDay,
    isEqual,
    isBefore,
    parse,
    startOfToday,
    isSameMonth,
    isSameDay,
    isAfter
} from 'date-fns';

export interface CalendarInterface {
    selectedDay?: null | Date | number;
    endDay?: null | Date | number;
    setEndDay: Function;
    setSelectedDay: Function;
    calendarStyle?: CSSProperties;
    selectPastDays?: boolean;
    containerStyle?: CSSProperties;
    datesStyle?: Object
  }

export default function CalendarDual({
    endDay,
    setEndDay,
    selectedDay,
    setSelectedDay,
    calendarStyle,
    selectPastDays,
    containerStyle,
    datesStyle,
}: CalendarInterface) {
    const today = startOfToday();
    const [currentMonth, setCurrentMonth] = useState(format(selectedDay || today, 'MMM-yyyy'))
    const firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date());
    const [currentMonthRight, setCurrentMonthRight] = useState(format(add(firstDayCurrentMonth, {months: 1}), 'MMM-yyyy'))
    const firstDayCurrentMonthRight = parse(currentMonthRight, 'MMM-yyyy', new Date());

    const days = eachDayOfInterval({
      start: firstDayCurrentMonth,
      end: endOfMonth(firstDayCurrentMonth),
    });
    const daysRight = eachDayOfInterval({
        start: firstDayCurrentMonthRight,
        end: endOfMonth(firstDayCurrentMonthRight),
      });
    const CalendarComponent = useRef<HTMLDivElement>(null);

    const firstDayPastMonth = add(firstDayCurrentMonth, { months: -1 });
    const firstDayPastMonthRight = add(firstDayCurrentMonthRight, { months: -1 });

    const firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
    const firstDayNextMonthRight = add(firstDayCurrentMonthRight, { months: 1 });

    function previousMonth() {
        if (!selectPastDays && isSameMonth(firstDayCurrentMonth, today)) return;
        let firstDayPreviousMonth = add(firstDayCurrentMonth, { months: -1 });
        let firstDayPreviousMonthRight = add(firstDayCurrentMonthRight, { months: -1 });
        setCurrentMonth(format(firstDayPreviousMonth, 'MMM-yyyy'))
        setCurrentMonthRight(format(firstDayPreviousMonthRight, 'MMM-yyyy'))
    }
    
    function nextMonth() {
        let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
        let firstDayNextMonthRight = add(firstDayCurrentMonthRight, { months: 1 });
        setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'));
        setCurrentMonthRight(format(firstDayNextMonthRight, 'MMM-yyyy'))
      }
    const pastMonthDays = eachDayOfInterval({
      start: firstDayPastMonth,
      end: endOfMonth(firstDayPastMonth),
    });
    const pastMonthDaysRight = eachDayOfInterval({
        start: firstDayPastMonthRight,
        end: endOfMonth(firstDayPastMonthRight),
      });

    const nextMonthDays = eachDayOfInterval({
      start: firstDayNextMonth,
      end: endOfMonth(firstDayNextMonth),
    });

    const nextMonthDaysRight = eachDayOfInterval({
      start: firstDayNextMonthRight,
      end: endOfMonth(firstDayNextMonthRight),
    });

    function handleSelectedDay(day: Date) {
        if (!selectPastDays && isBefore(day, today)) return;
        if (selectedDay && isSameDay(selectedDay, day)) {
            if (endDay) return setEndDay(undefined)
            else return setSelectedDay(null)
        }
        if (selectedDay && isBefore(day, selectedDay)) {
            return setSelectedDay(day)
        }
        if (selectedDay && isAfter(day, selectedDay)) {
            return setEndDay(day)
        }
        setSelectedDay(day);
    }
    return (
    <Main 
    ref={CalendarComponent}
    style={containerStyle}
    >
        <div className="calendar-wrapper left-calendar" style={datesStyle}>
            <button onClick={previousMonth} className="previous">
                <AiOutlineArrowLeft/>
            </button>
            <Container
            style={calendarStyle}
            >
              <Header>
                <span>
                  {format(firstDayCurrentMonth, 'MMM')}
                  <span> </span>
                  {format(firstDayCurrentMonth, 'yyyy')}
                </span>
              </Header>
              <hr />
              <div className="daysweek">
                <div>Mon</div>
                <div>Tue</div>
                <div>Wen</div>
                <div>Thu</div>
                <div>Fri</div>
                <div>Sat</div>
                <div>Sun</div>
              </div>
              <div className="days">
                {pastMonthDays
                  .slice(
                    getDay(days[0]) !== 0 ? pastMonthDays.length - getDay(days[0]) + 1 : pastMonthDays.length - 6,
                    pastMonthDays.length,
                  )
                  .map((day, dayIdx) => {
                    let className = '';
                    if (selectedDay && isEqual(day, selectedDay)) className = 'selected'
                    if (endDay && isEqual(day, endDay)) className = 'selected'
                    return (
                      <div key={dayIdx} className={className}>
                        <button onClick={() => handleSelectedDay(day)} className="past">
                          <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                        </button>
                      </div>
                    );
                  })}
                {days.map((day, dayIdx) => {
                  let className = '';
                  if (selectedDay && isEqual(day, selectedDay)) className = 'selected'
                  if (endDay && isEqual(day, endDay)) className = 'selected'
                  return (
                    <div
                      key={dayIdx}
                      style={
                        dayIdx === 0
                          ? getDay(day) === 0
                            ? { gridColumn: '7/8' }
                            : { gridColumn: `${getDay(day)} / span 1` }
                          : {}
                      }
                      className={className}
                    >
                      <button onClick={() => handleSelectedDay(day)} className={!selectPastDays ? (isBefore(day, today) ? 'past' : '') : ''}>
                        <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                      </button>
                    </div>
                  );
                })}
                {nextMonthDays
                  .slice(0, getDay(days[days.length - 1]) ? 7 - getDay(days[days.length - 1]) : 0)
                  .map((day, dayIdx) => {
                    let className = '';
                    if (selectedDay && isEqual(selectedDay, day)) className = 'selected'
                    return (
                      <div key={dayIdx} className={className}>
                        <button onClick={() => handleSelectedDay(day)} className="next">
                          <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                        </button>
                      </div>
                    );
                  })}
              </div>
            </Container>
        </div>
        {/* right month */}
        <div className="calendar-wrapper right-calendar" style={datesStyle}>
            <button onClick={nextMonth} className="next">
                <AiOutlineArrowRight/>
            </button>
            <Container
            style={calendarStyle}
            >
              <Header>
                <span>
                  {format(firstDayCurrentMonthRight, 'MMM')}
                  <span> </span>
                  {format(firstDayCurrentMonthRight, 'yyyy')}
                </span>
              </Header>
              <hr />
              <div className="daysweek">
                <div>Mon</div>
                <div>Tue</div>
                <div>Wen</div>
                <div>Thu</div>
                <div>Fri</div>
                <div>Sat</div>
                <div>Sun</div>
              </div>
              <div className="days">
                {pastMonthDaysRight
                  .slice(
                    getDay(daysRight[0]) !== 0 ? pastMonthDaysRight.length - getDay(daysRight[0]) + 1 : pastMonthDaysRight.length - 6,
                    pastMonthDaysRight.length,
                  )
                  .map((day, dayIdx) => {
                    let className = '';
                    if (endDay && isEqual(day, endDay)) className = 'selected'
                    return (
                      <div key={dayIdx} className={className}>
                        <button onClick={() => handleSelectedDay(day)} className="past">
                          <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                        </button>
                      </div>
                    );
                  })}
                {daysRight.map((day, dayIdx) => {
                  let className = '';
                  if (selectedDay && isEqual(day, selectedDay)) className = 'selected'
                  if (endDay && isEqual(day, endDay)) className = 'selected'
                  return (
                    <div
                      key={dayIdx}
                      style={
                        dayIdx === 0
                          ? getDay(day) === 0
                            ? { gridColumn: '7/8' }
                            : { gridColumn: `${getDay(day)} / span 1` }
                          : {}
                      }
                      className={className}
                    >
                      <button onClick={() => handleSelectedDay(day)} className={!selectPastDays ? (isBefore(day, today) ? 'past' : '') : ''}>
                        <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                      </button>
                    </div>
                  );
                })}
                {nextMonthDaysRight
                  .slice(0, getDay(daysRight[daysRight.length - 1]) ? 7 - getDay(daysRight[daysRight.length - 1]) : 0)
                  .map((day, dayIdx) => {
                    let className = '';
                    if (selectedDay && isEqual(selectedDay, day)) className = 'selected'
                    return (
                      <div key={dayIdx} className={className}>
                        <button onClick={() => handleSelectedDay(day)} className="next">
                          <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                        </button>
                      </div>
                    );
                  })}
              </div>
            </Container>
        </div>
    </Main>
    )
}

const Main = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    background-color: ${props => props.theme.white};
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    gap: 1px;
    width: fit-content;
    max-width: 100%;
    @media (max-width: 768px) {
        flex-direction: column;
        width: 100%;
        max-width: 100%;
    }
    
    .calendar-wrapper {
        position: relative;
        padding: 0;
        overflow: hidden;
        padding-bottom: 10px;
        display: flex;
        align-items: flex-start;
        flex: 1;
        min-width: 280px;
        
        @media (max-width: 768px) {
            width: 100%;
            min-width: auto;
        }
        
        > button {
            top: 20px;
            position: absolute;
            background-color: rgba(0, 0, 0, 0);
            border: none;
            width: 32px;
            height: 32px;
            cursor: pointer;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #000;
            z-index: 2;
            transition: background-color 0.2s;
            
            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
            }
            
            @media (max-width: 768px) {
                width: 32px;
                height: 32px;
                top: 16px;
            }
        }
        
        > button.previous {
            left: 10px;
        }
        
        > button.next {
            right: 10px;
        }
        
        &.left-calendar {
            border-top-left-radius: 16px;
            border-bottom-left-radius: 16px;
            
            @media (max-width: 768px) {
                border-top-left-radius: 16px;
                border-top-right-radius: 16px;
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
        }
        
        &.right-calendar {
            border-top-right-radius: 16px;
            border-bottom-right-radius: 16px;
            
            @media (max-width: 768px) {
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-bottom-left-radius: 16px;
                border-bottom-right-radius: 16px;
                margin-top: 1px;
            }
        }
    }
`;

const Container = styled.div`
    background-color: ${props => props.theme.white};
    padding: 8px;
    width: 100%;
    box-sizing: border-box;
    
    hr {
        margin: 12px 0;
        border: none;
        border-top: 1px solid #e5e7eb;
    }
    
    .daysweek {
        width: 100%;
        margin: 20px 0 8px 0;
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 4px;
        text-align: center;
        font-size: 13px;
        font-weight: 500;
        color: #6b7280;
        
        @media (max-width: 768px) {
            font-size: 12px;
            gap: 4px;
            margin: 16px 0 8px 0;
        }
    }
    
    .days {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        
        @media (max-width: 768px) {
            gap: 2px;
        }
        
        > div {
            margin: 0;
            font-size: 13px;
            font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            height: 32px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            
            @media (max-width: 768px) {
                height: 36px;
                font-size: 13px;
            }
        }
        
        .next,
        .past {
            color: #9ca3af;
        }
        
        button {
            width: 32px;
            height: 32px;
            background-color: rgba(0, 0, 0, 0);
            border: none;
            cursor: pointer;
            border-radius: 50%;
            color: #1f2937;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            margin: 0 auto;
            padding: 0;
            font-size: 13px;
            
            &:hover {
                background-color: #f3f4f6;
            }
            
            @media (max-width: 768px) {
                width: 34px;
                height: 34px;
                font-size: 13px;
            }
        }
        
        > div.selected {
            button {
                background: ${(props) => props.theme.blueDark || '#3b82f6'};
                color: white;
                
                &:hover {
                    background: ${(props) => props.theme.blueDark || '#3b82f6'};
                    opacity: 0.9;
                }
            }
        }
    }
`;

const Header = styled.div`
    padding: 12px 16px;
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    
    @media (max-width: 768px) {
        padding: 12px 16px;
        font-size: 14px;
    }
`;